import {
  Cascader,
  Checkbox,
  Input,
  InputNumber,
  Radio,
  Select,
} from '@seiue/ui'
import { isArray, omit } from '@seiue/util'
import { UploadFile } from 'antd'
import { DatePicker } from 'packages/components/DatePicker'
import { DatePickerRange } from 'packages/components/DatePickerRange'
import { DateTypeEnum, FieldTypeEnum } from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import { getPcaRegionOption } from 'packages/utils'
import React, { useContext } from 'react'
import styled from 'styled-components'

import { FormContext } from '../context'
import {
  ReqColumnWithMetaData,
  ReqWithMetaData,
  SelectTypeEnum,
  Option,
} from '../types'

import { ProductItem } from './ProductItem'
import { FileUpload } from './UploadFile'

/* eslint-disable max-lines-per-function */

/**
 * 问卷 - 表单项选择组件
 *
 * @param param -  参数
 * @param param.currentIndex - 当前题目序号
 * @param param.currentQuestion - 当前题目
 * @param param.isProductChild - 是否是复合组件的子项
 * @param param.productName -  若为复合组件子项则需要传入完成name
 * @param param.onChange - 组件状态修改方法
 * @param param.value - 组件值
 * @param param.fileList - 文件列表
 * @param param.id - 用于滚动到第一个错误的位置
 * @returns React.ReactNode
 */
export const SelectFormItem: React.FC<{
  currentIndex: number
  currentQuestion: ReqWithMetaData | ReqColumnWithMetaData
  onChange?: (v?: AnyType) => void
  value?: AnyType
  fileList?: UploadFile[]
  isProductChild?: boolean
  productName?: string
  id?: string
}> = ({
  currentIndex,
  currentQuestion,
  onChange,
  value,
  fileList,
  isProductChild,
  productName,
  id,
}) => {
  const { otherInfo, setOtherInfo, formValues } = useContext(FormContext)
  const { type, metadata, label, options } = currentQuestion

  const getFormItem = () => {
    let showMultiOtherInfo = false
    let showRadioOtherInfo = false
    const otherInfoKey =
      productName || currentQuestion.name || `pv-${currentQuestion.index}`
    if (!isProductChild) {
      if (currentQuestion.type === FieldTypeEnum.Multi) {
        showMultiOtherInfo =
          isArray(formValues[otherInfoKey]) &&
          formValues[otherInfoKey].includes('其它')
      }
      if (currentQuestion.type === FieldTypeEnum.Single) {
        showRadioOtherInfo = formValues[otherInfoKey] === '其它'
      }
    } else {
      const [parentName, childName, index] = otherInfoKey.split('.')
      try {
        const childrenValue = formValues[parentName]
          ? formValues[parentName][index]
          : undefined
        if (childrenValue) {
          if (currentQuestion.type === FieldTypeEnum.Multi) {
            showMultiOtherInfo =
              isArray(childrenValue[childName]) &&
              childrenValue[childName].includes('其它')
          }
          if (currentQuestion.type === FieldTypeEnum.Single) {
            showRadioOtherInfo = childrenValue[childName] === '其它'
          }
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error('otherInfo setting error')
      }
    }
    const isErrorStatus = metadata?.otherRequire && !otherInfo[otherInfoKey]
    switch (type) {
      case FieldTypeEnum.String: {
        return (
          <Input
            placeholder="请输入"
            maxLength={1000}
            onChange={onChange}
            value={value}
          />
        )
      }
      case FieldTypeEnum.Text: {
        return (
          <Input.TextArea
            onChange={onChange}
            placeholder="输入文本"
            style={{
              minHeight: '82px',
            }}
            maxLength={10000}
            value={value}
          />
        )
      }
      case FieldTypeEnum.Integer: {
        return (
          <InputNumber
            onChange={onChange}
            placeholder="请输入"
            max={10000}
            min={-10000}
            precision={metadata?.precision || 1}
            value={value}
          />
        )
      }
      case FieldTypeEnum.Date: {
        return (
          <DatePicker
            dateType={DateTypeEnum.Date}
            placeholder="请选择日期"
            allowClear={true}
            value={value}
            onChange={onChange}
          />
        )
      }
      case FieldTypeEnum.Time: {
        return (
          <DatePicker
            dateType={metadata?.dateType || DateTypeEnum.Time}
            placeholder="请选择日期"
            allowClear={true}
            value={value}
            onChange={onChange}
          />
        )
      }
      case FieldTypeEnum.DateRange: {
        return (
          <DatePickerRange
            onChange={onChange}
            placeholder={['开始日期', '结束日期']}
            dateType={metadata?.dateType}
            value={value}
          />
        )
      }
      case FieldTypeEnum.Attachment: {
        return (
          <FileUpload
            onChange={onChange}
            maxCount={metadata?.maxCount}
            fileSizeLimit={metadata?.fileSizeLimit}
            fileList={fileList}
          />
        )
      }
      case FieldTypeEnum.Address: {
        return (
          <Cascader
            onChange={onChange}
            placeholder="请选择"
            options={getPcaRegionOption()}
            value={value}
          />
        )
      }
      case FieldTypeEnum.Describe: {
        return (
          <Description>
            <Input.TextArea
              disabled={true}
              value={label}
              autoSize={{ minRows: 1 }}
              style={{ fontSize: 18 }}
            />
          </Description>
        )
      }
      case FieldTypeEnum.Single: {
        return (
          <>
            {metadata?.selectType === SelectTypeEnum.DropDown ? (
              <>
                <Select
                  onChange={(v) => {
                    if (v !== '其它') {
                      const newInfo = omit(otherInfo, otherInfoKey)
                      setOtherInfo({ ...newInfo })
                    } else {
                      metadata?.otherRequire &&
                        setOtherInfo({
                          ...otherInfo,
                          [otherInfoKey]: otherInfo[otherInfoKey] || undefined,
                        })
                    }
                    onChange && onChange(v)
                  }}
                  options={options}
                  placeholder="请选择"
                  allowClear={true}
                  value={value}
                />
                {showRadioOtherInfo ? (
                  <OtherInputWrapper>
                    <Input
                      placeholder="请输入"
                      style={{ marginTop: '8px' }}
                      onChange={(e) => {
                        const v = e.target.value
                        if (!v.trim() && metadata?.otherRequire) {
                          setOtherInfo({
                            ...otherInfo,
                            [otherInfoKey]: undefined,
                          })
                        } else {
                          setOtherInfo({
                            ...otherInfo,
                            [otherInfoKey]: v,
                          })
                        }
                      }}
                      value={otherInfo[otherInfoKey]}
                    />
                    {showRadioOtherInfo && isErrorStatus ? (
                      <ErrorWrapper $selectType={metadata.selectType}>
                        此项为必填项
                      </ErrorWrapper>
                    ) : null}
                  </OtherInputWrapper>
                ) : null}
              </>
            ) : (
              <Radio.Group
                onChange={(e) => {
                  const v = e.target.value
                  if (v !== '其它') {
                    const newInfo = omit(otherInfo, otherInfoKey)
                    setOtherInfo({ ...newInfo })
                  } else {
                    metadata?.otherRequire &&
                      setOtherInfo({
                        ...otherInfo,
                        [otherInfoKey]: otherInfo[otherInfoKey] || undefined,
                      })
                  }
                  onChange && onChange(v)
                }}
                value={value}
              >
                {options?.map((option: Option) => {
                  if (option.value === '其它') {
                    return (
                      <div key={option.value}>
                        <Radio
                          key={option.value}
                          value="其它"
                        >
                          <OtherWrapper>
                            <div>其它</div>
                            <Input
                              disabled={!showRadioOtherInfo}
                              placeholder="请输入"
                              style={{
                                width: isProductChild ? '582px' : '618px',
                                height: '32px',
                              }}
                              value={otherInfo[otherInfoKey]}
                              onChange={(e) => {
                                const v = e.target.value
                                if (!v.trim() && metadata?.otherRequire) {
                                  setOtherInfo({
                                    ...otherInfo,
                                    [otherInfoKey]: undefined,
                                  })
                                } else {
                                  setOtherInfo({
                                    ...otherInfo,
                                    [otherInfoKey]: v,
                                  })
                                }
                              }}
                            />
                          </OtherWrapper>
                        </Radio>
                        {showRadioOtherInfo && isErrorStatus ? (
                          <ErrorWrapper $selectType={metadata.selectType}>
                            此项为必填项
                          </ErrorWrapper>
                        ) : null}
                      </div>
                    )
                  }
                  return (
                    <Radio
                      key={option.value}
                      value={option.value}
                    >
                      <div
                        style={{
                          width: isProductChild ? '625px' : '655px',
                          overflow: 'wrap',
                        }}
                      >
                        {option.label}
                      </div>
                    </Radio>
                  )
                })}
              </Radio.Group>
            )}
          </>
        )
      }
      case FieldTypeEnum.Multi: {
        return (
          <>
            {metadata?.selectType === SelectTypeEnum.DropDown ? (
              <>
                <Select
                  onChange={(v) => {
                    if (!v.includes('其它')) {
                      const newInfo = omit(otherInfo, otherInfoKey)
                      setOtherInfo({ ...newInfo })
                    } else {
                      metadata?.otherRequire &&
                        setOtherInfo({
                          ...otherInfo,
                          [otherInfoKey]: otherInfo[otherInfoKey] || undefined,
                        })
                    }
                    onChange && onChange(v)
                  }}
                  options={options}
                  placeholder="请选择"
                  mode="multiple"
                  allowClear={true}
                  value={value}
                />
                {showMultiOtherInfo ? (
                  <OtherInputWrapper>
                    <Input
                      placeholder="请输入"
                      style={{ marginTop: '8px' }}
                      onChange={(e) => {
                        const v = e.target.value
                        if (!v.trim() && metadata?.otherRequire) {
                          setOtherInfo({
                            ...otherInfo,
                            [otherInfoKey]: undefined,
                          })
                        } else {
                          setOtherInfo({
                            ...otherInfo,
                            [otherInfoKey]: v,
                          })
                        }
                      }}
                      value={otherInfo[otherInfoKey]}
                    />
                    {showMultiOtherInfo && isErrorStatus ? (
                      <ErrorWrapper $selectType={metadata.selectType}>
                        此项为必填项
                      </ErrorWrapper>
                    ) : null}
                  </OtherInputWrapper>
                ) : null}
              </>
            ) : (
              <Checkbox.Group
                onChange={(v) => {
                  if (!(isArray(v) && v.includes('其它')) || !v) {
                    const newInfo = omit(otherInfo, otherInfoKey)
                    setOtherInfo({ ...newInfo })
                  } else {
                    metadata?.otherRequire &&
                      setOtherInfo({
                        ...otherInfo,
                        [otherInfoKey]: otherInfo[otherInfoKey] || undefined,
                      })
                  }
                  onChange && onChange(v)
                }}
                value={value}
              >
                {options?.map((option: Option) => {
                  if (option.value === '其它') {
                    return (
                      <div key={option.value}>
                        <Checkbox
                          key={option.value}
                          value="其它"
                        >
                          <OtherWrapper>
                            <div>其它</div>
                            <Input
                              disabled={!showMultiOtherInfo}
                              placeholder="请输入"
                              style={{
                                width: isProductChild ? '582px' : '618px',
                                height: '32px',
                              }}
                              onChange={(e) => {
                                const v = e.target.value
                                if (!v.trim() && metadata?.otherRequire) {
                                  setOtherInfo({
                                    ...otherInfo,
                                    [otherInfoKey]: undefined,
                                  })
                                } else {
                                  setOtherInfo({
                                    ...otherInfo,
                                    [otherInfoKey]: v,
                                  })
                                }
                              }}
                              value={otherInfo[otherInfoKey]}
                            />
                          </OtherWrapper>
                        </Checkbox>
                        {showMultiOtherInfo && isErrorStatus ? (
                          <ErrorWrapper $selectType={metadata.selectType}>
                            此项为必填项
                          </ErrorWrapper>
                        ) : null}
                      </div>
                    )
                  }
                  return (
                    <Checkbox
                      key={option.value}
                      value={option.value}
                    >
                      <div
                        style={{
                          width: isProductChild ? '625px' : '655px',
                          overflow: 'wrap',
                        }}
                      >
                        {option.label}
                      </div>
                    </Checkbox>
                  )
                })}
              </Checkbox.Group>
            )}
          </>
        )
      }
      case FieldTypeEnum.Product: {
        return (
          <ProductItem
            currentQuestion={currentQuestion}
            currentIndex={currentIndex}
          />
        )
      }
    }
  }
  return <Wrapper id={id}>{getFormItem()}</Wrapper>
}

const Wrapper = styled.div`
  &&& {
    .ant-form-item {
      margin-bottom: 0;
    }
    .ant-input {
      height: 40px;
      padding: 8px 16px;
    }
    .ant-input-number {
      width: 100%;
      height: 40px;
      border-radius: 8px;
      background-color: ${(props) => props.theme.background._3};
      border: 1px solid transparent;
      & .ant-input-number-input-wrap {
        height: 40px;
        line-height: 40px;
      }
      & .ant-input-number-handler-wrap {
        border-radius: 8px;
        background-color: ${(props) => props.theme.background._3};
      }
      & .ant-input-number-input {
        padding-left: 16px;
      }
    }
    .ant-picker,
    .ant-picker-range {
      padding: 8px 16px;
      width: 100%;
      height: 40px;
    }
    .ant-select,
    .ant-select-selector {
      min-height: 40px;
    }
    .ant-select-selector {
      /* padding: 6px 16px; */

      line-height: 24px;
      .ant-select-selection-item,
      .ant-select-selection-placeholder {
        line-height: 40px;
      }
      .ant-select-selection-overflow {
        padding-top: 5px;
      }
    }
    .ant-select-multiple {
      min-height: 40px;
    }
    .ant-select-selection-overflow {
      top: -2px;
    }
    .ant-select-selection-item-content,
    .ant-select-selection-item-remove {
      line-height: 22px;
    }
    .ant-radio-group,
    .ant-checkbox-group {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      margin-left: 0;
      .ant-checkbox-wrapper {
        margin-left: 0;
      }
    }
    .ant-radio-checked .ant-radio-inner::after {
      background-color: ${(props) => props.theme.brand._1};
    }
    .ant-radio-inner,
    .ant-checkbox-inner {
      border-color: ${(props) => props.theme.brand._1} !important;
    }
  }
`

const Description = styled.div`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: ${(props) => props.theme.text._2};
  &&& {
    .ant-input,
    .ant-input:hover {
      background-color: transparent !important;
      color: ${(props) => props.theme.text._2} !important;
      pointer-events: none;
      padding: 0 0;
    }
  }
`

const OtherWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  & > div:first-child {
    width: 30px;
    margin-right: 10px;
    font-size: 14px;
  }
`

const OtherInputWrapper = styled.div``

const ErrorWrapper = styled.div<{ $selectType?: SelectTypeEnum }>`
  color: #f54545;
  font-size: 12px;
  margin-left: ${(props) =>
    props.$selectType === SelectTypeEnum.Checked ? '65px' : '0'};
  margin-top: 5px;
`
